import { useMemo } from 'react';
import { useArbiterClient } from '../context/ArbiterClientContext';
import { usePaginatedApi } from './usePaginatedApi';
import type { RuleSummary, ListRulesInput } from '@amzn/helianthus-arbiter-client';

interface UseRulesResult {
  rules: RuleSummary[];
  loading: boolean;
  fetchingMore: boolean;
  error: string | null;
  refetch: () => void;
}

export function useRules(
  params: Omit<ListRulesInput, 'continuationToken'> = {},
  options?: { enabled?: boolean },
): UseRulesResult {
  const client = useArbiterClient();

  // usePaginatedApi keys its effect on JSON.stringify(params), so identity does not matter; only the
  // empty-string-to-undefined normalization does.
  const fetchParams = {
    resourceType: params.resourceType || undefined,
    ruleStatus: params.ruleStatus || undefined,
    limit: params.limit,
  };

  const { data, loading, fetchingMore, error, refetch } = usePaginatedApi(
    (fetchParams) =>
      client.listRules(fetchParams).then((r) => ({
        items: r.rules ?? [],
        continuationToken: r.continuationToken,
      })),
    fetchParams,
    options,
  );

  const rules = useMemo(() => {
    // Deduplicate: keep only the latest version per ruleName
    const latestByName = new Map<string, RuleSummary>();
    for (const rule of data) {
      const name = rule.ruleName ?? '';
      const existing = latestByName.get(name);
      if (!existing || (Date.parse(rule.lastModifiedAt ?? '') || 0) > (Date.parse(existing.lastModifiedAt ?? '') || 0)) {
        latestByName.set(name, rule);
      }
    }

    // Filter by status when a specific status is requested
    const values = Array.from(latestByName.values());
    if (params.ruleStatus) {
      return values.filter((rule) => rule.ruleStatus === params.ruleStatus);
    }
    // Default: filter out inactive rules
    return values.filter((rule) => rule.ruleStatus !== 'inactive');
  }, [data, params.ruleStatus]);

  return { rules, loading, fetchingMore, error, refetch };
}
